import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { memo, useCallback, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { TextHeadline } from '@cbhq/cds-web/typography';
import { SearchInput, SelectOption } from '@cbhq/cds-web/controls';
import { SelectChip } from '@cbhq/cds-web/chips';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { TokenizedEquitiesRow } from './TokenizedEquitiesRow';
import { TokenizedEquitiesMobileRow } from './TokenizedEquitiesMobileRow';
import {
  useTokenizedEquitiesData,
  type TokenizedEquityData,
} from './hooks/useTokenizedEquitiesData';

type SortDirection = 'asc' | 'desc' | null;

// Columns for the Tokenized Equities table
// Widths: Token=25%, Stock Price=18%, Token Price=19%, Token Market Cap=19%, Token Volume=19%
const TokenizedEquitiesColumns = [
  { label: 'Token', field: 'name', width: '25%' },
  { label: 'Stock Price', field: 'stockPriceUsd', width: '18%' },
  { label: 'Token Price', field: 'avgTokenPriceUsd', width: '19%' },
  { label: 'Token Market Cap', field: 'tokenMarketCapUsd', width: '19%' },
  { label: 'Token Volume', field: 'tokenVolumeUsd', width: '19%' },
];

function TokenizedEquitiesRowFallback() {
  return (
    <TableRow>
      {TokenizedEquitiesColumns.map(({ label }, i) =>
        i === 0 ? (
          <TableCell
            key={label}
            start={<Fallback width={20} height={20} shape="circle" disableRandomRectWidth />}
          >
            <HStack alignItems="center" gap={1}>
              <Fallback width={100} height={24} disableRandomRectWidth />
              <Fallback width={60} height={24} disableRandomRectWidth />
            </HStack>
          </TableCell>
        ) : (
          <TableCell key={label} alignItems="flex-start">
            <VStack gap={0.5}>
              <Fallback width={70} height={20} disableRandomRectWidth />
              <Fallback width={40} height={14} disableRandomRectWidth />
            </VStack>
          </TableCell>
        ),
      )}
    </TableRow>
  );
}

function TokenizedEquitiesMobileFallback() {
  return (
    <TableRow>
      <TableCell>
        <HStack gap={1.5} alignItems="center" width="100%">
          <Fallback width={40} height={40} shape="circle" disableRandomRectWidth />
          <VStack gap={0.5} flexGrow={1}>
            <Fallback width={100} height={20} disableRandomRectWidth />
            <Fallback width={60} height={14} disableRandomRectWidth />
          </VStack>
          <VStack gap={0.5} alignItems="flex-end">
            <Fallback width={60} height={20} disableRandomRectWidth />
            <Fallback width={40} height={14} disableRandomRectWidth />
          </VStack>
        </HStack>
      </TableCell>
    </TableRow>
  );
}

function TokenizedEquitiesItemsLoading({ isMobile }: { isMobile: boolean }) {
  const rows = [0, 1, 2, 3];
  return (
    <>
      {rows.map((i) =>
        isMobile ? (
          <TokenizedEquitiesMobileFallback key={i} />
        ) : (
          <TokenizedEquitiesRowFallback key={i} />
        ),
      )}
    </>
  );
}

type TokenizedEquitiesItemsProps = {
  search: string;
  sortField: string | null;
  sortDirection: SortDirection;
  onEquitySelect?: (tokenAddress: string) => void;
  isMobile: boolean;
};

function TokenizedEquitiesItems({
  search,
  sortField,
  sortDirection,
  onEquitySelect,
  isMobile,
}: TokenizedEquitiesItemsProps) {
  const { equities, isLoading } = useTokenizedEquitiesData();

  const equitiesList = useMemo(() => {
    if (isLoading) return [];

    // First filter by search
    let filteredEquities = equities.filter(
      (equity) =>
        equity.name.toLowerCase().includes(search.toLowerCase()) ||
        equity.symbol.toLowerCase().includes(search.toLowerCase()),
    );

    // Then sort if needed
    if (sortField && sortDirection) {
      filteredEquities = [...filteredEquities].sort((a, b) => {
        // Handle string fields (like name) separately
        if (sortField === 'name') {
          return sortDirection === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        }

        // For numeric fields
        const aValue = a[sortField as keyof TokenizedEquityData] as number;
        const bValue = b[sortField as keyof TokenizedEquityData] as number;

        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }

    return filteredEquities;
  }, [equities, isLoading, search, sortField, sortDirection]);

  if (isLoading) {
    return <TokenizedEquitiesItemsLoading isMobile={isMobile} />;
  }

  return (
    <>
      {equitiesList.map((equity) =>
        isMobile ? (
          <TokenizedEquitiesMobileRow key={equity.id} equity={equity} onSelect={onEquitySelect} />
        ) : (
          <TokenizedEquitiesRow key={equity.id} equity={equity} onSelect={onEquitySelect} />
        ),
      )}
    </>
  );
}

type TokenizedEquitiesListContentProps = {
  onEquitySelect?: (tokenAddress: string) => void;
};

const TokenizedEquitiesListContent = memo(function TokenizedEquitiesListContent({
  onEquitySelect,
}: TokenizedEquitiesListContentProps) {
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const isMobile = useMemo(() => Boolean(isPhone || isPhoneLandscape), [isPhone, isPhoneLandscape]);

  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        // Cycle through: asc -> desc -> null
        const newDirection =
          sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc';
        setSortDirection(newDirection);
        if (newDirection === null) {
          setSortField(null);
        }
      } else {
        // Start with ascending for a new field
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField, sortDirection],
  );

  return (
    <>
      {/* Filter and Search Section */}
      <VStack paddingY={2} paddingX={3}>
        <HStack alignItems="center" justifyContent="space-between" gap={2}>
          {/* Filter dropdown */}
          <SelectChip
            value="Assets"
            content={<SelectOption value="Assets" title="Assets" />}
            background="bgAlternate"
            height={42}
          >
            Assets
          </SelectChip>

          {/* Search input */}
          <SearchInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search"
            compact
            width={isMobile ? 220 : 240}
          />
        </HStack>
      </VStack>

      {/* Table */}
      <VStack maxWidth="100%">
        <Table>
          {!isMobile && (
            <TableHeader>
              <TableRow>
                {TokenizedEquitiesColumns.map(({ label, field, width }) => {
                  const isSorted = sortField === field;
                  const iconName = !isSorted
                    ? undefined
                    : sortDirection === 'asc'
                    ? 'sortUp'
                    : 'sortDown';

                  const headerContent = (
                    <HStack alignItems="center" gap={0.5}>
                      <TextHeadline as="span" noWrap color="fgMuted">
                        {label}
                      </TextHeadline>
                      {iconName && <Icon name={iconName} size="xs" color="fg" />}
                    </HStack>
                  );

                  return (
                    <TableCell
                      key={label}
                      width={width}
                      alignItems="flex-start"
                      onClick={field !== 'name' ? () => handleSort(field) : undefined}
                      testID={`sort-column-${field}`}
                    >
                      {headerContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHeader>
          )}
          <TableBody>
            <TokenizedEquitiesItems
              search={search}
              sortField={sortField}
              sortDirection={sortDirection}
              onEquitySelect={onEquitySelect}
              isMobile={isMobile}
            />
          </TableBody>
        </Table>
      </VStack>
    </>
  );
});

type TokenizedEquitiesListProps = {
  onEquitySelect?: (tokenAddress: string) => void;
};

export const TokenizedEquitiesList = memo(function TokenizedEquitiesList({
  onEquitySelect,
}: TokenizedEquitiesListProps) {
  return (
    <VStack background="bg" borderRadius={300}>
      <TokenizedEquitiesListContent onEquitySelect={onEquitySelect} />
    </VStack>
  );
});
